import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BillingAgreement, Currency } from '@freelancer/datastore/collections';
import { methodSelectionRadioControl } from '@freelancer/payments-utils';
import { CardBorderRadius } from '@freelancer/ui/card';
import { fromPairs, isDefined } from '@freelancer/utils';
import {
  DepositMethodApi,
  VIRTUAL_PAYMENT_TYPEApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DepositDataMap } from '../methods/methods.component';
import {
  mapValueToSelectedCurrencies,
  MethodPaymentCurrency,
  MethodSelectedCurrencies,
} from '../payments.helpers';
import { CreditCardBillingAgreementComponent } from './credit-card-billing-agreement/credit-card-billing-agreement.component';
import { PaypalBillingAgreementComponent } from './paypal-billing-agreement/paypal-billing-agreement.component';

export type BillingAgreementComponent =
  | PaypalBillingAgreementComponent
  | CreditCardBillingAgreementComponent;
@Component({
  selector: 'app-billing-agreements',
  template: `
    <ng-container *ngFor="let agreement of billingAgreements$ | flAsync">
      <ng-container [ngSwitch]="agreement.depositMethod">
        <app-paypal-billing-agreement
          #billingAgreement
          *ngSwitchCase="DepositMethodApi.PAYPAL_REFERENCE"
          [agreement]="agreement"
          [group]="group"
          [currencies]="currencies$ | flAsync"
          [depositData]="depositDataMap[agreement.id] | flAsync"
          [trackingToken]="trackingToken"
          [cartId]="cartId"
        ></app-paypal-billing-agreement>
        <app-credit-card-billing-agreement
          #billingAgreement
          *ngSwitchCase="DepositMethodApi.FLN_BILLING"
          [agreement]="agreement"
          [group]="group"
          [currencies]="currencies$ | flAsync"
          [depositData]="depositDataMap[agreement.id] | flAsync"
          [trackingToken]="trackingToken"
          [cartId]="cartId"
        ></app-credit-card-billing-agreement>
      </ng-container>
      <app-virtual-billing-agreement
        #billingAgreement
        *ngIf="isVirtual(agreement)"
        [agreement]="agreement"
        [group]="group"
        [currencies]="currencies$ | flAsync"
        [depositData]="depositDataMap[agreement.id] | flAsync"
        [trackingToken]="trackingToken"
        [cartId]="cartId"
      ></app-virtual-billing-agreement>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./billing-agreements.component.scss'],
})
export class BillingAgreementsComponent implements AfterViewInit, OnDestroy {
  private subscriptions: readonly Rx.Subscription[] = [];
  initialQueryList: QueryList<BillingAgreementComponent>;
  CardBorderRadius = CardBorderRadius;
  DepositMethodApi = DepositMethodApi;

  @Input() billingAgreements$: Rx.Observable<readonly BillingAgreement[]>;
  @Input() depositDataMap: DepositDataMap;
  @Input() group: FormGroup;
  @Input() currencies$: Rx.Observable<readonly Currency[]>;
  @Input() trackingToken: string;
  @Input() cartId?: number;

  @Output()
  agreementPaymentCurrencySelected = new EventEmitter<MethodPaymentCurrency>();

  @ViewChildren('billingAgreement')
  set setBillingAgreement(queryList: QueryList<BillingAgreementComponent>) {
    if (this.initialQueryList) {
      return;
    }
    this.initialQueryList = queryList;
  }

  @ViewChildren('billingAgreement')
  billingAgreementQueryList: QueryList<BillingAgreementComponent>;

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngAfterViewInit() {
    // Due to some angular nuance we miss the first `changes` emission
    // ( likely to do with a surrounding *ngIf )
    // To account for this, we catch the first emission in the
    // `set` function above and manually startWith it.
    const agreementSelectedCurrencies$: Rx.Observable<MethodSelectedCurrencies> = this.billingAgreementQueryList.changes.pipe(
      startWith<QueryList<BillingAgreementComponent>>(this.initialQueryList),
      map(queryList =>
        fromPairs(
          queryList
            .map(el =>
              el.paymentCurrencySelected
                ? ([
                    el.agreement.id,
                    el.paymentCurrencySelected.asObservable(),
                  ] as const)
                : undefined,
            )
            .filter(isDefined),
        ),
      ),
    );

    const control = this.group.get(methodSelectionRadioControl);
    if (control) {
      const currency$ = mapValueToSelectedCurrencies(
        control,
        agreementSelectedCurrencies$,
      );
      this.subscriptions = [
        ...this.subscriptions,
        currency$.subscribe(v => {
          this.agreementPaymentCurrencySelected.emit(v);
        }),
      ];
    }
  }

  isVirtual(agreement: BillingAgreement) {
    return VIRTUAL_PAYMENT_TYPEApi.includes(agreement.depositMethod);
  }
}
